import type { User } from '@supabase/supabase-js';
import {
  RECOVERY_ABANDONED_MESSAGE,
  RECOVERY_INACTIVE_USER_MESSAGE,
  RECOVERY_SESSIONS_NOT_ENDED_MESSAGE,
  classifyRecoveryRequest,
  classifyRecoveryUpdate,
  classifyRecoveryVerify,
  type RecoveryRequestResult,
  type RecoveryUpdateOutcome,
  type RecoveryVerifyOutcome,
} from '@joshua/domain/recoveryErrors';

import { supabase } from '@/lib/supabase';
import * as authService from '@/services/auth.service';
import { NotFoundError } from '@/services/errors';

/**
 * JOSH-73: a user who forgot the password recovers it with a code mailed by
 * Supabase Auth, in three steps:
 *   1. `resetPasswordForEmail(email)` (no `redirectTo`: the code is typed);
 *   2. `verifyOtp({ email, token, type: 'recovery' })`, which opens a session;
 *   3. `updateUser({ password })`, then `signOut({ scope: 'others' })`.
 *
 * Step 2 opens a full session before the new password exists, so a
 * "recovery pending" flag keeps the app closed until step 3 succeeds. The flag
 * lives in localStorage next to the Supabase session, so every tab sees it,
 * and holds only the email and a timestamp, never the code. AuthContext reads
 * it synchronously in the auth listener and ignores sessions while it is set.
 *
 * Every exit clears the flag: a failed verify, a completed recovery, an
 * abandonment after verify (close, cancel, idle) and the cold-start cleanup
 * of a flag left by a reload or a new tab. Abandonment signs the device out
 * locally first: the code is spent, so the person asks for a new one.
 *
 * Exits that sign out fail closed: the flag is cleared only once the stored
 * session is confirmed gone. supabase-js `signOut({ scope: 'local' })` calls
 * the server first and, on a network failure, returns the error WITHOUT
 * removing the stored session; `signOutLocally` then removes it itself. If it
 * still remains, the flag stays and keeps every tab gated until a later
 * cleanup (cold start, a new verify or a password sign-in) replaces it.
 *
 * Never call a bare `signOut()` here: its default scope is global and it ends
 * every session of the user (and can answer 403).
 */

export const RECOVERY_FLAG_KEY = 'joshua_recovery_pending';

/** Idle time on a recovery step after which the UI abandons the recovery. */
export const RECOVERY_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

export interface RecoveryFlag {
  email: string;
  startedAt: number;
}

export function normalizeRecoveryEmail(email: string): string {
  return email.trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Flag (synchronous: read inside the onAuthStateChange callback)
// ---------------------------------------------------------------------------

export function readRecoveryFlag(): RecoveryFlag | null {
  try {
    const raw = localStorage.getItem(RECOVERY_FLAG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RecoveryFlag> | null;
    return {
      email: typeof parsed?.email === 'string' ? parsed.email : '',
      startedAt: typeof parsed?.startedAt === 'number' ? parsed.startedAt : 0,
    };
  } catch {
    // A corrupt value still means a recovery was in flight: keep the gate shut
    // so the cleanup runs.
    try {
      return localStorage.getItem(RECOVERY_FLAG_KEY) ? { email: '', startedAt: 0 } : null;
    } catch {
      return null;
    }
  }
}

export function isRecoveryPending(): boolean {
  return readRecoveryFlag() !== null;
}

export function setRecoveryFlag(email: string): void {
  const flag: RecoveryFlag = { email: normalizeRecoveryEmail(email), startedAt: Date.now() };
  try {
    localStorage.setItem(RECOVERY_FLAG_KEY, JSON.stringify(flag));
  } catch {
    // Storage unavailable (private mode): the gate cannot persist. The verify
    // still runs; the in-tab state keeps the app closed until completion.
  }
}

export function clearRecoveryFlag(): void {
  try {
    localStorage.removeItem(RECOVERY_FLAG_KEY);
  } catch {
    // Ignore storage errors.
  }
}

/** supabase-js internals used as a fail-closed fallback. Not public API. */
interface AuthClientInternals {
  _removeSession?: () => Promise<void>;
}

/** True only when the client confirms no stored session (error = still there). */
async function isStoredSessionGone(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.getSession();
    return !error && !data.session;
  } catch {
    return false;
  }
}

/**
 * Drops the session on this device only and confirms it is gone. When the
 * server call fails (offline), supabase-js keeps the stored session, so it is
 * removed through the client's own `_removeSession` (storage keys plus the
 * SIGNED_OUT event). Returns false when the session is still stored: callers
 * must then keep the recovery flag set.
 */
export async function signOutLocally(): Promise<boolean> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) console.warn('[recuperarSenha] Local sign-out failed:', error);
  } catch (err) {
    console.warn('[recuperarSenha] Local sign-out failed:', err);
  }
  if (await isStoredSessionGone()) return true;

  // Cast the object, never detach the method: `_removeSession` uses `this`.
  const auth = supabase.auth as unknown as AuthClientInternals;
  if (typeof auth._removeSession === 'function') {
    try {
      await auth._removeSession();
    } catch (err) {
      console.error('[recuperarSenha] Could not remove the stored session:', err);
    }
  } else {
    console.error('[recuperarSenha] supabase-js has no _removeSession; the session may remain.');
  }

  const gone = await isStoredSessionGone();
  if (!gone) console.error('[recuperarSenha] Session still stored after local sign-out.');
  return gone;
}

/** Local sign-out, then the flag, which is cleared only if the session is gone. */
async function endRecoverySession(): Promise<boolean> {
  const gone = await signOutLocally();
  if (gone) clearRecoveryFlag();
  return gone;
}

// ---------------------------------------------------------------------------
// Step 1: request
// ---------------------------------------------------------------------------

/**
 * Asks Auth to mail a recovery code. Every outcome except "no connection" and
 * the project-wide email limit answers with the same neutral message, so the
 * screen never reveals whether the account exists.
 */
export async function requestRecoveryCode(email: string): Promise<RecoveryRequestResult> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(normalizeRecoveryEmail(email));
    return classifyRecoveryRequest(error);
  } catch (err) {
    return classifyRecoveryRequest(err);
  }
}

// ---------------------------------------------------------------------------
// Step 2: verify
// ---------------------------------------------------------------------------

/**
 * `abandoned`: Auth accepted the code but the account check after it could not
 * run; the code is spent and the device was signed out locally.
 */
export type VerifyRecoveryFailure = RecoveryVerifyOutcome | 'abandoned';

export type VerifyRecoveryResult =
  | { ok: true; userId: string; email: string }
  | { ok: false; outcome: VerifyRecoveryFailure; message: string };

/**
 * Checks the code. The flag is set before `verifyOtp`, so the session it opens
 * never loads the app, and stays set on success until `completeRecovery`.
 * A deactivated user is signed out locally with the generic failure message,
 * which does not reveal the account state (R19).
 */
export async function verifyRecoveryCode(email: string, code: string): Promise<VerifyRecoveryResult> {
  const normalized = normalizeRecoveryEmail(email);
  const token = code.replace(/\s+/g, '');

  setRecoveryFlag(normalized);

  let userId: string | null = null;
  try {
    const { data, error } = await supabase.auth.verifyOtp({ email: normalized, token, type: 'recovery' });
    if (error || !data.session || !data.user) {
      clearRecoveryFlag();
      const { outcome, message } = classifyRecoveryVerify(error);
      return { ok: false, outcome, message };
    }
    userId = data.user.id;
  } catch (err) {
    clearRecoveryFlag();
    const { outcome, message } = classifyRecoveryVerify(err);
    return { ok: false, outcome, message };
  }

  // Second layer behind the Auth ban (U14): the same lookup AuthContext runs
  // on every sign-in. Fails closed: without the check the recovery stops.
  try {
    const pessoa = await authService.getPessoaByUserId(userId);
    if (pessoa.user_ativo === false) {
      await endRecoverySession();
      return { ok: false, outcome: 'blocked', message: RECOVERY_INACTIVE_USER_MESSAGE };
    }
  } catch (err) {
    await endRecoverySession();
    if (err instanceof NotFoundError) {
      return { ok: false, outcome: 'blocked', message: RECOVERY_INACTIVE_USER_MESSAGE };
    }
    return { ok: false, outcome: 'abandoned', message: RECOVERY_ABANDONED_MESSAGE };
  }

  return { ok: true, userId, email: normalized };
}

// ---------------------------------------------------------------------------
// Step 3: new password
// ---------------------------------------------------------------------------

export type CompleteRecoveryResult =
  | {
      ok: true;
      user: User;
      /** Non-blocking notice when the other sessions could not be ended now. */
      notice: string | null;
    }
  | {
      ok: false;
      outcome: RecoveryUpdateOutcome;
      message: string;
      /**
       * True when the recovery is over (session lost, user blocked): the device
       * was signed out locally, the flag cleared, and the UI returns to the
       * request step. False keeps the person on the new-password step.
       */
      abandoned: boolean;
    };

/**
 * Saves the new password, ends the other sessions and clears the flag. The
 * caller sets the user from the returned session user: no auth event follows
 * (`USER_UPDATED` fired while the flag was still set, and
 * `signOut({ scope: 'others' })` emits nothing locally).
 */
export async function completeRecovery(password: string): Promise<CompleteRecoveryResult> {
  // A recovery abandoned meanwhile (another tab, cold-start cleanup) has no
  // session left: updateUser fails with AuthSessionMissingError → session_lost.
  try {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return await failUpdate(error);
  } catch (err) {
    return await failUpdate(err);
  }

  let notice: string | null = null;
  try {
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    if (error) notice = RECOVERY_SESSIONS_NOT_ENDED_MESSAGE;
  } catch {
    notice = RECOVERY_SESSIONS_NOT_ENDED_MESSAGE;
  }

  const { data } = await supabase.auth.getSession();
  const user = data.session?.user ?? null;
  clearRecoveryFlag();

  if (!user) {
    return { ok: false, outcome: 'session_lost', message: RECOVERY_ABANDONED_MESSAGE, abandoned: true };
  }
  return { ok: true, user, notice };
}

async function failUpdate(err: unknown): Promise<CompleteRecoveryResult> {
  const { outcome, message } = classifyRecoveryUpdate(err);
  if (outcome === 'session_lost' || outcome === 'blocked') {
    // The flag stays if the session could not be removed (fail closed).
    await endRecoverySession();
    return { ok: false, outcome, message, abandoned: true };
  }
  // Weak, leaked or same password, network, unexpected: the person stays on
  // the step with the flag set and can try again.
  return { ok: false, outcome, message, abandoned: false };
}

// ---------------------------------------------------------------------------
// Abandonment and cold start
// ---------------------------------------------------------------------------

export interface RecoveryAbandonment {
  /** The email to prefill on the request step ('' when unknown). */
  email: string;
  message: string;
}

/**
 * Ends a recovery left after verify (close, cancel, idle) or found on a cold
 * start: local sign-out, then the flag is cleared. Returns null when no
 * recovery was pending (closing before verify needs no cleanup).
 *
 * When the session could not be removed the flag stays set (callers check
 * `isRecoveryPending()` to keep their gate closed); the person still goes back
 * to the request step, since the code is spent either way.
 */
export async function abandonRecovery(): Promise<RecoveryAbandonment | null> {
  const flag = readRecoveryFlag();
  if (!flag) return null;
  await endRecoverySession();
  return { email: flag.email, message: RECOVERY_ABANDONED_MESSAGE };
}
